import { createFileRoute, Link } from '@tanstack/react-router';
import { ArrowLeft, FileSearch } from 'lucide-react';
import { DataText } from '../components/data/data-text';
import { EmptyState } from '../components/guidance/empty-state';
import { Tip } from '../components/guidance/tip';
import { useScreenTour } from '../components/guidance/tour';
import { PageHeader } from '../components/layout/app-shell';
import { SheetView } from '../components/sheets/sheet-view';
import { ApiError } from '../lib/api';
import { useSchema } from '../lib/schema';
import { useSheet } from '../lib/sheets';

// Profile sheet import (task 12b, PLAN §5 steps 2b–2c): what the template importer read from one Actor Profile
// Sheet, and — for a newer version of a sheet — only what changed since the earlier one.

export const Route = createFileRoute('/sheets/$id')({ component: SheetPage });

const TOUR = {
  id: 'sheet-import',
  steps: [
    {
      target: '[data-tour="sheet-summary"]',
      title: 'Mapped without AI',
      body: 'The template importer reads the sheet block by block. This is the share of table rows it placed in a field or column by itself.',
      learnMore: 'import-a-profile-sheet',
    },
    {
      target: '[data-tour="sheet-blocks"]',
      title: 'Block by block',
      body: 'Each row shows the text as written, how it was read, and whether it needs a decision in review (a value outside a list, a missing grade…).',
    },
  ],
};

function SheetPage() {
  useScreenTour(TOUR);
  const { id } = Route.useParams();
  const sheet = useSheet(id);
  const { data: snap } = useSchema();
  if (sheet.isError)
    return (
      <EmptyState
        icon={FileSearch}
        title={
          sheet.error instanceof ApiError && sheet.error.status === 404 ? 'No profile sheet import here' : 'The import could not be loaded'
        }
        learnMore="import-a-profile-sheet"
      >
        Only files recognised as Actor Profile Sheets are read with the template. Other files are extracted by the AI step.
      </EmptyState>
    );
  if (!sheet.data) return <p className="text-sm text-muted-foreground">Loading…</p>;
  const { document: doc, import: imp, versions } = sheet.data;
  const s = imp.sheet;
  const template = snap?.templates.find(t => t.id === s.templateId);
  return (
    <div className="space-y-6">
      <Link to="/sheets" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="size-4" aria-hidden="true" /> All imported sheets
      </Link>
      <PageHeader
        title={s.displayName ?? doc.filename}
        description={[
          s.code,
          template ? `${template.name} v${template.version}` : null,
          s.sheetVersion ? `sheet v${s.sheetVersion}` : null,
          doc.filename,
        ]
          .filter(Boolean)
          .join(' · ')}
      />
      <Tip id="sheet-import" learnMore="import-a-profile-sheet">
        Nothing here is in the graph yet. Mapped items become candidates for review; a newer version of a sheet sends only its changes.
      </Tip>
      <div className="grid gap-6 lg:grid-cols-[1fr_16rem]">
        <SheetView detail={sheet.data} snap={snap} />
        <aside aria-labelledby="sheet-versions" className="space-y-2 self-start rounded-lg border bg-surface p-4 text-sm">
          <h2 id="sheet-versions" className="font-semibold">
            Versions of <DataText>{s.code ?? 'this sheet'}</DataText>
          </h2>
          <ol className="space-y-1">
            {versions.map(v => (
              <li key={v.id}>
                <Link
                  to="/sheets/$id"
                  params={{ id: v.id }}
                  aria-current={v.id === doc.id ? 'page' : undefined}
                  className="block truncate rounded px-1 py-0.5 hover:bg-muted aria-[current=page]:font-semibold"
                >
                  <DataText>{v.filename}</DataText>
                </Link>
                <p className="px-1 text-xs text-muted-foreground">
                  {new Date(v.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                  {v.supersedesDocumentId ? ' · update' : ' · first import'}
                </p>
              </li>
            ))}
          </ol>
        </aside>
      </div>
    </div>
  );
}
